/** Pure persistence boundary, independently testable without browser/auth/API bootstrapping. */
export interface DurablePosAttempt {
  key: string;
  kind: "return" | "credit_sale";
  body: Record<string, unknown>;
}
type AttemptStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function readDurablePosAttempt(storage: AttemptStorage, storageKey: string): DurablePosAttempt | null {
  const raw = storage.getItem(storageKey);
  if (!raw) return null;
  const value = JSON.parse(raw) as DurablePosAttempt;
  if (typeof value.key !== "string" || !value.key || !["return", "credit_sale"].includes(value.kind)
    || !value.body || typeof value.body !== "object" || Array.isArray(value.body))
    throw new Error("alkokh.posReturns.storageFailed");
  return value;
}

export function persistDurablePosAttempt(storage: AttemptStorage, storageKey: string, kind: DurablePosAttempt["kind"], body: Record<string, unknown>, key: string) {
  if (readDurablePosAttempt(storage, storageKey)) throw new Error("alkokh.posReturns.pending");
  const attempt: DurablePosAttempt = { key, kind, body: JSON.parse(JSON.stringify(body)) };
  const serialized = JSON.stringify(attempt);
  storage.setItem(storageKey, serialized);
  if (storage.getItem(storageKey) !== serialized) throw new Error("alkokh.posReturns.storageFailed");
  return attempt;
}

/** Another tab may have already recovered this attempt and started a new one. */
export function releaseDurablePosAttempt(storage: AttemptStorage, storageKey: string, attemptKey: string) {
  if (readDurablePosAttempt(storage, storageKey)?.key === attemptKey) storage.removeItem(storageKey);
}

export function validPosReturnTotals(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return ["return_total", "replacement_total", "debt_reduction", "collect_amount", "cash_refund", "credit_issued"]
    .every(key => typeof row[key] === "number" && Number.isFinite(row[key]) && Number(row[key]) >= 0);
}

/** A typed HTTP response alone is insufficient evidence to forget a financial request. */
export function validatePosAttemptResult(kind: DurablePosAttempt["kind"], result: unknown): boolean {
  if (!result || typeof result !== "object") return false;
  const value = result as Record<string, any>;
  if (kind === "credit_sale")
    return typeof value.invoice?.id === "string" && Boolean(value.invoice.id)
      && value.receipt?.invoiceId === value.invoice.id && Array.isArray(value.receipt?.items)
      && Number.isFinite(value.receipt?.totalAmount) && Number.isFinite(value.receipt?.returnCreditAmount);
  return typeof value.operation === "string" && Boolean(value.operation)
    && validPosReturnTotals(value.summary);
}
